import { IPromise } from 'angular';

export const AiCreditsServiceName = 'gpAiCreditsService';

/**
 * $rootScope event asking the credits panel to re-fetch the balance — broadcast by
 * the purchase panel after a confirmed top-up so the new credits show immediately.
 */
export const AiCreditsRefreshEvent = 'gpAiCredits:refresh';

/** A single product or feature that consumed AI credits in the current period. */
export interface AiCreditProductUsage {
    /** Stable key for the product/feature (not shown to the user). */
    productKey: string;
    /** Display name, e.g. 'Chat', 'AI Planner', 'Risk Analysis'. */
    productName: string;
    /** Whole credits consumed by this product this period. */
    creditsUsed: number;
    /**
     * True for free features (Coach): creditsUsed is the real cost of the usage
     * but it is never charged to any balance, so it must never be counted
     * towards credits used.
     */
    nonBillable?: boolean;
}

/** Per-member usage, used when credits are a shared team pool. */
export interface AiCreditMemberUsage {
    userId: string;
    displayName: string;
    creditsUsed: number;
}

/**
 * One credit pool the user can spend from (#724): their own personal pool, or a
 * team they are an active member of.
 *
 * Balances only — no billing period, reset date or usage breakdown. Those come
 * from Stripe, which the server only asks about for the pool being billed, so
 * they exist on {@link AiCreditBalance} for that pool alone and nowhere else.
 */
export interface AiCreditPool {
    /**
     * Whether this pool belongs to the user themselves or to a team. Matches the
     * wording the rest of the credits API uses ('user'/'team'), NOT the
     * 'personal'/'team' wording of {@link AiCreditBalance.scope}.
     */
    ownerType: 'user' | 'team';
    /** Identity user id, or team id (a GUID string). Stable key for the row. */
    ownerId: string;
    /**
     * The team's name. Explicitly null for the personal pool — the client picks
     * its own wording for that one ("Your credits", "Personal", …).
     */
    name: string | null;
    /**
     * True on the one pool the user's AI usage is currently billed to. Exactly
     * one pool in the array has this set, and every top-level field on
     * {@link AiCreditBalance} describes that same pool.
     */
    isCurrent: boolean;
    /**
     * Whether this user may buy top-up credits for THIS pool: always true for
     * their personal pool, team-owner/billing-manager only for a team pool
     * (#76, #465). Purchase rights are per pool, so a user can be able to top up
     * one of their teams and not another.
     */
    canPurchase: boolean;
    /**
     * Whether this pool has an allotment position worth showing at all — it has
     * been granted allotment at some point, or holds a non-zero allotment balance
     * now. When false the UI must show NOTHING about an allotment for this pool:
     * no bar, no "0 credits", no reset wording. A pool that has never had a plan
     * allotment does not have an empty one. Also false for a pool that does not
     * count toward the bar and has no allotment left (#1686).
     */
    hasAllotment: boolean;
    /**
     * Allotment credits still available in this pool. Only meaningful when
     * {@link hasAllotment} is true.
     */
    allotmentRemaining: number;
    /**
     * Allotment spent in this pool's current cycle — since
     * {@link allotmentCycleStartUtc}, or across all of its history when that is null.
     * Consumption only, so credits forfeited by a sweep are not counted as used.
     */
    allotmentUsed: number;
    /**
     * What this pool's current cycle made available: {@link allotmentUsed} +
     * {@link allotmentRemaining}. The denominator for a usage bar over this pool.
     *
     * Not the same quantity as {@link AiCreditBalance.monthlyAllotment}, which is what
     * the BILLED pool was granted inside its Stripe billing period. That figure exists
     * for one pool only and can disagree with the balance beside it (a pool holding
     * credits carried over from an earlier period holds more than it was granted this
     * one); this one is derived from the ledger, agrees with `allotmentRemaining`, and
     * exists for every pool.
     */
    allotmentTotal: number;
    /**
     * Whether a paying subscription will post this pool's next allotment (#1686).
     * False for a personal pool whose subscription now belongs to a team. Absent from
     * servers that predate #1686.
     */
    allotmentRenews?: boolean;
    /**
     * Whether this pool's allotment belongs in the panel's one summed "allotted credits
     * used" bar (#1686): it renews or is billed, except that internal staff count only
     * their personal pool. Absent from servers that predate #1686, and absent must read
     * as true: every pool was summed then.
     */
    countsTowardAllotment?: boolean;
    /**
     * When the cycle behind {@link allotmentUsed} began (ISO 8601 UTC): the pool's
     * latest subscription payment or sweep. Null when it has had none, or without
     * {@link hasAllotment}; absent from servers that predate #1686.
     */
    allotmentCycleStartUtc?: string | null;
    /** Purchased top-up credits still available in this pool. Never reset. */
    purchasedRemaining: number;
    /** Purchased credits consumed, all-time (purchasedTotal - purchasedRemaining). */
    purchasedUsed: number;
    /**
     * All-time purchased credits ever added to this pool (paid top-ups +
     * promotional grants). 0 when the pool has never had purchased credits.
     */
    purchasedTotal: number;
}

/**
 * A user's (or team's) AI credit position for the current billing period.
 *
 * Credits are always whole numbers. The user-facing model is "1 credit = 1 cent
 * of plan value"; the underlying provider cost and any internal cost multiplier
 * are never represented here and must never be shown in the UI. This contract is
 * what the real backend service will implement; the mock service mirrors it.
 *
 * Every field here except {@link pools} describes the BILLED pool — the one the
 * server chose for this user's AI spend (a team pool that has credits, else their
 * personal pool). {@link pools} is the full list, including that one.
 */
export interface AiCreditBalance {
    /** Whether the BILLED pool belongs to a single user or is shared across a team. */
    scope: 'personal' | 'team';
    /**
     * Account tier: 'staff' renews monthly outside Stripe; 'free' has no plan
     * allotment (welcome credits arrive as one-time purchased promos); 'paid'
     * follows the subscription.
     */
    tier?: 'staff' | 'free' | 'paid';
    /** Present when scope === 'team'. */
    teamName?: string;

    /**
     * Whether this user may buy top-up credits for the pool: always true for a
     * personal pool, team-owner/billing-manager only for a team pool (#76).
     */
    canPurchase?: boolean;

    /** Credits granted for the current period. Resets on each billing payment. */
    monthlyAllotment: number;
    /** Credits used from the allotment so far this period. */
    allotmentUsed: number;
    /** Credits still available from the allotment this period. */
    allotmentRemaining: number;

    /**
     * The internal-staff monthly account. Separate from the subscription triple
     * above because it renews on the UTC calendar month regardless of any paid
     * subscription's billing period (#716) — a staff subscriber carries both.
     * Zero when the user has no staff grant this month. Optional: absent from
     * older server payloads.
     */
    staffAllotmentMonthly?: number;
    /** Credits used from the staff allotment so far this calendar month. */
    staffAllotmentUsed?: number;
    /** Credits still available from the staff allotment this calendar month. */
    staffAllotmentRemaining?: number;

    /** Purchased top-up credits still available. These never reset. */
    purchasedRemaining: number;
    /**
     * Purchased credits consumed so far, all-time (purchasedTotal - purchasedRemaining).
     * The filled portion of the purchased bar.
     */
    purchasedUsed: number;
    /**
     * All-time purchased credits ever added (paid top-ups + promotional grants). The
     * denominator of the purchased bar. 0 when the owner has never had purchased credits.
     */
    purchasedTotal: number;

    /** Billing cadence that drives the reset-period wording ('month' or 'year'). */
    billingInterval?: 'month' | 'year';

    /** When the allotment next resets (ISO 8601 UTC). */
    periodResetsUtc?: string;

    /**
     * When the staff allotment next resets: the 1st of the next UTC month
     * (ISO 8601). Null/absent when there is no staff grant this month.
     * Independent of periodResetsUtc — the accounts renew on different cadences.
     */
    staffResetsUtc?: string;

    /** Breakdown of this period's allotment usage by product/feature. */
    usageByProduct: AiCreditProductUsage[];
    /** Optional breakdown by team member, when scope === 'team'. */
    usageByMember?: AiCreditMemberUsage[];

    /**
     * Every pool this user can spend from (#724): their personal pool first, then
     * each team they are an active member of, in the order they joined — which is
     * also the order the server falls through when picking where to bill, so the
     * list reads as the reason the current pool is the current one.
     *
     * The server leaves out a pool with nothing left to show (#1686): one that does
     * not count toward the allotment bar and has neither allotment nor purchased
     * credits left. So the personal pool can be missing. The billed pool never is:
     * the list is never empty and exactly one row has {@link AiCreditPool.isCurrent}.
     */
    pools: AiCreditPool[];
}

export interface IAiCreditsApi {
    getBalance(): IPromise<AiCreditBalance>;
}
